import { Model, Attr, BelongsTo, HasOne } from 'spraypaint';
import { User, Media } from '@/api';
import dayjs from 'dayjs';
import axios from 'axios';
import Cookies from 'js-cookie';

import ApplicationRecord from '../ApplicationRecord';
import SurveySite from './SurveySite';

export enum FaunaSurveyStatus {
  draft = 'draft',
  published = 'published',
  inProgress = 'in_progress',
  assessed = 'assessed',
  verified = 'verified',
  unknown = 'unknown',
}

export const faunaSurveyStatusItems = [
  {
    label: 'Draft',
    value: 'draft',
  },
  {
    label: 'Published',
    value: 'published',
  },
  {
    label: 'In Progress',
    value: 'in_progress',
  },
  {
    label: 'Complete',
    value: 'assessed',
  },
];

@Model()
export default class FaunaSurvey extends ApplicationRecord {
  static jsonapiType = 'fauna-surveys';

  static listHeaders() {
    return [
      {
        text: 'ID',
        value: 'id',
        width: 100,
      },
      {
        text: 'Site',
        value: 'surveySiteName',
      },
      {
        text: 'Status',
        value: 'status',
      },
      {
        text: 'Created',
        value: 'createdAt',
      },
    ];
  }

  static scopeFactory() {
    return FaunaSurvey.where({ skip_stats: true }).includes([]);
  }

  @Attr() createdAt: string;

  @Attr() startTimestamp: string;

  @Attr() endTimestamp: string;

  @Attr() status: FaunaSurveyStatus;

  // no longer a thing
  // @HasOne() verifiedBy: User;

  @HasOne() assessedBy: User | null;

  @HasOne() createdBy: User;

  // aggregate counts for detection bbox tag status
  @Attr() readonly tagStatusNone: number;

  @Attr() readonly tagStatusConflict: number;

  @Attr() readonly tagStatusAgreed: number;

  @Attr() readonly tagStatusResolved: number;

  // aggregate counts for detection bbox tag status
  @Attr() readonly snsStatusNone: number;

  @Attr() readonly snsStatusPending: number;

  @Attr() readonly snsStatusUploaded: number;

  @Attr() readonly snsStatusDetected: number;

  @Attr() readonly snsStatusDetectFailed: number;

  @Attr() readonly snsStatusPredicted: number;

  @Attr() readonly snsStatusPredictFailed: number;

  // has no animal bboxes or bboxes is blank true
  @Attr() readonly blankMediaCount: number;

  // does not include pending
  // TODO: check if it includes failed status'
  @Attr() readonly faunaMediaCount: number;

  @Attr() readonly stickybeakVersions: number[];

  @BelongsTo() surveySite: SurveySite;

  @HasOne() readonly coverMedia: Media; // do i need this?

  // @HasMany() faunaMedia: FaunaMedia[];

  @Attr() readonly surveySiteName: string;

  get nonBlankMediaCount() {
    return this.faunaMediaCount - this.blankMediaCount;
  }

  get problematicCount() {
    return (
      this.snsStatusPending +
      this.snsStatusDetectFailed +
      this.snsStatusPredictFailed
    );
  }

  get uploadedWithoutErrorsCount() {
    return (
      this.snsStatusNone +
      this.snsStatusUploaded +
      this.snsStatusDetected +
      this.snsStatusPredicted
    );
  }

  // dont include pending!
  get uploadedCount() {
    return (
      this.snsStatusNone +
      // this.snsStatusPending +
      this.snsStatusUploaded +
      this.snsStatusDetected +
      this.snsStatusDetectFailed +
      this.snsStatusPredicted +
      this.snsStatusPredictFailed
    );
  }

  // dont include pending!
  get totalCount() {
    return (
      this.snsStatusNone +
      this.snsStatusPending +
      this.snsStatusUploaded +
      this.snsStatusDetected +
      this.snsStatusDetectFailed +
      this.snsStatusPredicted +
      this.snsStatusPredictFailed
    );
  }

  get totalBboxCount() {
    return (
      this.tagStatusNone +
      this.tagStatusConflict +
      this.tagStatusAgreed +
      this.tagStatusResolved
    );
  }

  get totalConflictCount() {
    return this.tagStatusConflict + this.tagStatusResolved;
  }

  get isProcessing() {
    return this.snsStatusPredicted < this.faunaMediaCount;
  }

  get formattedDate() {
    const start = dayjs(this.startTimestamp);
    const end = dayjs(this.endTimestamp);

    if (start.year() === end.year()) {
      return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
    }
    return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
  }

  get formattedCreatedDate() {
    return dayjs(this.createdAt).fromNow();
  }

  get prettyStatus() {
    switch (this.status) {
      case FaunaSurveyStatus.draft:
        return 'Draft';
      case FaunaSurveyStatus.published:
        return 'Published';
      case FaunaSurveyStatus.inProgress:
        return 'In progress';
      case FaunaSurveyStatus.assessed:
        return 'Complete';
      default:
        return 'Unknown';
    }
  }

  get createdName() {
    return `${this.createdBy.name || 'Unknown'}`;
  }

  get progressText() {
    const assessedByName =
      this.assessedBy && this.assessedBy.name
        ? this.assessedBy.name
        : 'Unknown';

    switch (this.status) {
      case FaunaSurveyStatus.draft:
        return 'Draft';
      case FaunaSurveyStatus.published:
        return 'Published';
      case FaunaSurveyStatus.inProgress:
        return `Assessment in progress by ${assessedByName}`;
      case FaunaSurveyStatus.assessed:
      case FaunaSurveyStatus.verified:
        return `Assessed by ${assessedByName} (Complete)`;
      default:
        return 'Unknown';
    }
  }

  async retryStickybeak() {
    if (!this.id) {
      throw new Error('Survey ID is required to retry Stickybeak');
    }
    const csrf = Cookies.get('csrftoken');
    const url = `${this.klass.url(this.id)}retry_stickybeak/`;
    try {
      const response = await axios.post(
        url,
        {},
        { headers: { 'X-CSRFTOKEN': csrf } },
      );
      return response.status === 201;
    } catch (error) {
      console.error('error retrying stickybeak', error);
    }
    return false;
  }
}
